import React from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from "chart.js";
import styles from "./consumptionCompareContent.module.scss";
import { ComparationType } from "../../enums/comparison-type";
import { useCompareMeterConsumptionEnergyUsageQuery } from "../../../../../service/consumption-service";
import { ConsumptionCompareLoadingContent } from "./consumptionCompareLoadingContent";
import { TriangleDownIcon } from "../../../../../assets/icons";
import { MainValue, MainValueTextColor } from "../../../../../common/components/main-value/MainValue";
import { useChartTokens } from "../../../../../theme/chart-tokens";
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const CHART_TOKENS = ["bannerError", "primaryText02", "primaryText05"] as const;
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function ordinal(day: number): string {
	if (day >= 11 && day <= 13) return `${day}th`;
	switch (day % 10) {
		case 1:
			return `${day}st`;
		case 2:
			return `${day}nd`;
		case 3:
			return `${day}rd`;
		default:
			return `${day}th`;
	}
}

function monthName(monthsAgo: number): string {
	const now = new Date();
	const shifted = new Date(now.getFullYear(), now.getMonth() - monthsAgo);
	return MONTH_NAMES[shifted.getMonth()];
}

function dayName(date: Date): string {
	const jsDay = date.getDay();
	return DAY_NAMES[jsDay === 0 ? 6 : jsDay - 1];
}

function getPreviousTitle(type: ComparationType): string {
	const now = new Date();
	switch (type) {
		case ComparationType.DoD: {
			const d = new Date(now);
			d.setDate(d.getDate() - 2);
			return `Day Before (${dayName(d)})`;
		}
		case ComparationType.WoW:
			return "Prior Week";
		case ComparationType.MoM:
			return monthName(2);
		case ComparationType.YoY:
			return `${now.getFullYear() - 2}`;
	}
}

function getCurrentTitle(type: ComparationType): string {
	const now = new Date();
	switch (type) {
		case ComparationType.DoD: {
			const d = new Date(now);
			d.setDate(d.getDate() - 1);
			return `Yesterday, ${ordinal(d.getDate())}`;
		}
		case ComparationType.WoW:
			return "Last Week";
		case ComparationType.MoM:
			return `Last Month (${monthName(1)})`;
		case ComparationType.YoY:
			return `Last Year (${now.getFullYear() - 1})`;
	}
}

function getSectionTitle(type: ComparationType): string {
	switch (type) {
		case ComparationType.DoD:
			return "Day over Day Comparison";
		case ComparationType.WoW:
			return "Week over Week Comparison";
		case ComparationType.MoM:
			return "Month over Month Comparison";
		case ComparationType.YoY:
			return "Year over Year Comparison";
	}
}

function getSubtitle(type: ComparationType, current: number, previous: number, percentage: number): string {
	const changeType = percentage >= 0 ? "decrease" : "increase";
	const absPercent = Math.abs(percentage);
	const suffix = type === ComparationType.DoD ? "DoD" : type === ComparationType.WoW ? "WoW" : type === ComparationType.MoM ? "MoM" : "YoY";
	switch (type) {
		case ComparationType.DoD:
			return `You used ${current} kWh yesterday, compared to ${previous} kWh the day before. That's a ${absPercent}% ${changeType} ${suffix}.`;
		case ComparationType.WoW:
			return `You used ${current} kWh last week, compared to ${previous} kWh the prior week. That's a ${absPercent}% ${changeType} ${suffix}.`;
		case ComparationType.MoM:
			return `You used ${current} kWh in ${monthName(1)}, compared to ${previous} kWh in ${monthName(2)}. That's a ${absPercent}% ${changeType} ${suffix}.`;
		case ComparationType.YoY: {
			const y = new Date().getFullYear();
			return `You used ${current} kWh in ${y - 1}, compared to ${previous} kWh in ${y - 2}. That's a ${absPercent}% ${changeType} ${suffix}.`;
		}
	}
}

function getPreviousChartLabel(type: ComparationType): string {
	const now = new Date();
	switch (type) {
		case ComparationType.DoD: {
			const d = new Date(now);
			d.setDate(d.getDate() - 2);
			return `${dayName(d)}, ${ordinal(d.getDate())}`;
		}
		case ComparationType.WoW:
			return "Prior Week";
		case ComparationType.MoM:
			return monthName(2);
		case ComparationType.YoY:
			return `${now.getFullYear() - 2}`;
	}
}

function getCurrentChartLabel(type: ComparationType): string {
	const now = new Date();
	switch (type) {
		case ComparationType.DoD: {
			const d = new Date(now);
			d.setDate(d.getDate() - 1);
			return `Yesterday, ${ordinal(d.getDate())}`;
		}
		case ComparationType.WoW:
			return "Last Week";
		case ComparationType.MoM:
			return monthName(1);
		case ComparationType.YoY:
			return `${now.getFullYear() - 1}`;
	}
}

interface Props {
	comparationType: ComparationType;
}

export const ConsumptionCompareContent: React.FC<Props> = ({ comparationType }) => {
	// We use isFetching to show the ConsumptionCompareLoadingContent because redux cache will trigger isLoading only
	// first time when the component is mounted, and isFetching will be triggered every time user change the tab from DoD to WoW etc.
	const { data, isFetching, error } = useCompareMeterConsumptionEnergyUsageQuery(comparationType, {
		refetchOnMountOrArgChange: true,
	});
	const chartColors = useChartTokens(CHART_TOKENS);

	if (isFetching) {
		return <ConsumptionCompareLoadingContent />;
	}

	const d = data ?? { previousPeriodValue: 0, currentPeriodValue: 0, percentage: 0 };
	const absPercent = Math.abs(d.percentage);

	// percentage > 0 means consumption decreased (savings = green), < 0 means increase (red)
	const percentStatusClass = d.percentage === 0 ? `${styles.neutralStatus}` : d.percentage > 0 ? `${styles.positiveStatus}` : `${styles.negativeStatus}`;

	const chartData = {
		labels: [getPreviousChartLabel(comparationType), getCurrentChartLabel(comparationType)],
		datasets: [
			{
				data: [d.previousPeriodValue, d.currentPeriodValue],
				backgroundColor: [chartColors.primaryText02, chartColors.bannerError],
				borderRadius: 4,
				barThickness: 80,
			},
		],
	};

	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: { callbacks: { label: (ctx: any) => `${ctx.parsed.y} kWh` } },
		},
		scales: {
			x: {
				grid: { display: false },
				ticks: { color: chartColors.primaryText05, font: { size: 12 } },
				border: { display: false },
			},
			y: { display: false },
		},
	};

	return (
		<div className={`${styles.wrapper} ${error ? styles.hideContent : ""}`}>
			<hr className={styles.divider} />
			<p className={styles.sectionTitle}>{getSectionTitle(comparationType)}</p>
			<p className={styles.subtitle}>{getSubtitle(comparationType, d.currentPeriodValue, d.previousPeriodValue, d.percentage)}</p>
			<hr className={styles.divider} />
			<div className={styles.metricsRow}>
				<div className={styles.metricGroup}>
					<span className={styles.metricLabel}>{getCurrentTitle(comparationType)}</span>
					<MainValue value={d.currentPeriodValue} unit="kWh" textColor={MainValueTextColor.Red} />
				</div>
				<div className={styles.metricGroup}>
					<span className={styles.metricLabel}>{getPreviousTitle(comparationType)}</span>
					<MainValue value={d.previousPeriodValue} unit="kWh" />
				</div>
				<span className={`${styles.percentBadge} ${percentStatusClass}`}>
					<TriangleDownIcon /> {absPercent}%
				</span>
			</div>
			<hr className={styles.divider} />
			<div className={styles.chartWrapper}>
				<Bar data={chartData} options={chartOptions as any} />
			</div>
			<p className={styles.footerText}>Comparison is based on your meter readings for each period.</p>
		</div>
	);
};
